import { SHIPPER_DOCUMENT_LABELS, type ShipperProfileView } from '@khaddi/contracts';
import { Button, Icon, LogoMark, Screen, Text, colors, radii, useAction, useSession, type IconName, brandSurface } from '@khaddi/ui';
import { View } from 'react-native';
import { InfoRows } from '../components/InfoRows';
import { shiftsLabel } from './AccountScreen';

type StatusCopy = { icon: IconName; title: string; body: string; tone: string };

const copyFor = (status: ShipperProfileView['status']): StatusCopy => {
  switch (status) {
    case 'pending':
      return { icon: 'file', title: 'Hồ sơ đang chờ duyệt', body: 'Quán sẽ xem hồ sơ và gọi cho bạn trong 1–2 ngày làm việc. Khi được duyệt, bạn có thể bật nhận đơn ngay.', tone: colors.gold };
    case 'rejected':
      return { icon: 'close', title: 'Hồ sơ chưa được duyệt', body: 'Hồ sơ của bạn chưa đạt yêu cầu. Kiểm tra lại giấy tờ, thông tin xe rồi gửi lại nhé.', tone: colors.red };
    case 'suspended':
      return { icon: 'lock', title: 'Tài khoản tạm khoá', body: 'Tài khoản shipper của bạn đang bị tạm khoá. Liên hệ quản lý quán để được hỗ trợ mở lại.', tone: colors.red };
    case 'active':
      return { icon: 'check', title: 'Hồ sơ đã được duyệt', body: 'Tải lại để bắt đầu nhận đơn.', tone: colors.mint };
  }
};

export type ApplicationStatusScreenProps = {
  profile: ShipperProfileView;
  onRefresh: () => Promise<void>;
  onReapply: () => void;
};

export const ApplicationStatusScreen = ({ profile, onRefresh, onReapply }: ApplicationStatusScreenProps) => {
  const { signOut } = useSession();
  const refresh = useAction(onRefresh);
  const copy = copyFor(profile.status);
  const missing = profile.missingDocuments;

  return (
    <Screen maxWidth={720}>
      <View style={{ ...brandSurface, paddingTop: 18, paddingHorizontal: 20, paddingBottom: 28, gap: 18, overflow: 'hidden' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <LogoMark size={40} />
          <View style={{ flex: 1 }}>
            <Text variant="caption" color={colors.text}>
              Khao Ddi Shipper
            </Text>
            <Text variant="bodyStrong" color={colors.text} style={{ fontSize: 16 }}>
              Chào {profile.name}
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', gap: 14, alignItems: 'flex-start' }}>
          <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: copy.tone, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={copy.icon} size={24} color={colors.navy} strokeWidth={2.4} />
          </View>
          <View style={{ flex: 1, gap: 6 }}>
            <View accessibilityRole="header"><Text variant="title" color={colors.text}>
              {copy.title}
            </Text></View>
            <Text variant="body" color={colors.text} style={{ fontSize: 13, lineHeight: 20 }}>
              {copy.body}
            </Text>
          </View>
        </View>
      </View>

      <View style={{ padding: 16, gap: 12 }}>
        {profile.status === 'pending' ? (
          <View style={{ backgroundColor: colors.surface, borderRadius: radii.lg, padding: 14, gap: 10 }}>
            <Text variant="heading">{missing.length ? 'Giấy tờ còn thiếu' : 'Giấy tờ đã đủ'}</Text>
            {missing.length ? (
              missing.map((doc) => (
                <View key={doc} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', minHeight: 32 }}>
                  <Icon name="camera" size={18} color={colors.danger} />
                  <Text variant="body">{SHIPPER_DOCUMENT_LABELS[doc]}</Text>
                </View>
              ))
            ) : (
              <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
                <Icon name="check" size={18} color={colors.green} strokeWidth={2.6} />
                <Text variant="body">Quán đã nhận đủ giấy tờ của bạn</Text>
              </View>
            )}
            {missing.length ? <Text variant="caption">Mang bản gốc giấy tờ còn thiếu khi đến quán phỏng vấn.</Text> : null}
          </View>
        ) : null}

        <InfoRows
          rows={[
            { label: 'Phương tiện', value: [profile.vehicleType, profile.vehicleModel].filter(Boolean).join(' · ') },
            { label: 'Biển số', value: profile.plate || '—' },
            { label: 'Khu vực', value: profile.areas.join(', ') || '—' },
            { label: 'Ca chạy', value: shiftsLabel(profile) },
          ]}
        />

        {refresh.error ? (
          <Text variant="caption" color={colors.danger}>
            {refresh.error}
          </Text>
        ) : null}
        {profile.status === 'rejected' ? <Button label="Đăng ký lại" onPress={onReapply} /> : null}
        {profile.status !== 'suspended' ? <Button label="Kiểm tra lại trạng thái" variant={profile.status === 'rejected' ? 'outline' : 'primary'} icon="history" onPress={() => void refresh.run()} loading={refresh.pending} /> : null}
        <Button label="Đăng xuất" variant="ghost" icon="logout" onPress={signOut} />
      </View>
    </Screen>
  );
};
